import { inject, Injectable } from '@angular/core';
import {
  addDoc,
  arrayRemove,
  arrayUnion,
  collection,
  collectionChanges,
  collectionData,
  doc,
  DocumentData,
  endBefore,
  Firestore,
  getDocs,
  limit,
  orderBy,
  query,
  Query,
  QueryDocumentSnapshot,
  startAfter,
  Timestamp,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { TranslocoService } from '@jsverse/transloco';
import { AuthenticationService } from '@modules/authentication/services/authentication.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Channel,
  ChannelsSearchOptions,
  Message,
  MessageGroup,
  MessageSenderType,
  searchChannels,
} from '@pixacare/chat';
import { ObjectType } from '@pixacare/domain';
import { CreateMessageRequest } from '@pixacare/dtos';
import { formatUsername } from '@pixacare/shared';
import { DraftMessage } from '@shared/models/chat/draft-message';
import { BehaviorSubject, map, Observable, Subscription } from 'rxjs';
import { ShareHttpService } from 'src/app/services/http/share.http.service';
import { ChatHttpService } from './chat.http.service';
import { MessageGroupService } from './message-group.service';

@UntilDestroy()
@Injectable()
export class ChatService {
  channels$: Observable<Channel[]>;
  currentMessages$: Observable<{ [messageId: string]: Message }>;
  hasUnseenMessages$: Observable<boolean>;
  unreadMessagesCounter$: Observable<number>;

  private readonly fireStore: Firestore = inject(Firestore);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly messageGroupService = inject(MessageGroupService);
  private readonly chatHttpService = inject(ChatHttpService);
  private readonly shareService = inject(ShareHttpService);
  private readonly translocoService = inject(TranslocoService);

  private currentMessages = new BehaviorSubject<{ [messageId: string]: Message }>({});
  private hasUnseenMessages = new BehaviorSubject<boolean>(false);
  private channelQuery: Query;

  private MessageSubscription: Subscription;

  private pageSize = 50;
  private lastCursor: Message = null;
  private firstCursor: Message = null;

  constructor() {
    const channelCollection = collection(this.fireStore, 'channels');
    this.channelQuery = query(
      channelCollection,
      where('userIds', 'array-contains', this.authenticationService.user.id),
      orderBy('lastMessage.createdOn', 'desc'),
    );
    this.channels$ = (collectionData(this.channelQuery, { idField: 'id' }) as Observable<Channel[]>).pipe(
      untilDestroyed(this),
    );
    this.currentMessages$ = this.currentMessages.asObservable();
    this.hasUnseenMessages$ = this.hasUnseenMessages.asObservable();
    this.unreadMessagesCounter$ = this.channels$.pipe(
      map((channels) =>
        channels.reduce(
          (totalUnreads, channel) => totalUnreads + channel?.unreads[this.authenticationService.user.id] || 0,
          0,
        ),
      ),
    );
  }

  markMessagesAsSeen(): void {
    this.hasUnseenMessages.next(false);
  }

  updateChannelUnreads(channelId: string): void {
    this.chatHttpService.updateChannelUserUnreads(channelId, this.authenticationService.user.id).subscribe();
  }

  getMessageGroups(): Observable<MessageGroup[]> {
    return this.messageGroupService.messageGroups$;
  }

  async updateNotifications(channelId: string, enabledPriorities: number[]): Promise<void> {
    const channelDocRef = doc(this.fireStore, `channels/${channelId}`);
    try {
      const userId = this.authenticationService.user.id;

      const value = (level: number): any =>
        enabledPriorities.includes(level) ? arrayUnion(userId) : arrayRemove(userId);

      await updateDoc(channelDocRef, {
        'settings.notifications.0': value(0),
        'settings.notifications.1': value(1),
        'settings.notifications.2': value(2),
        'settings.notifications.3': value(3),
      });
    } catch (e) {
      console.error('Error while updating channel notifications', e);
    }
  }

  getSenderName(message: Message): string {
    switch (message.senderType) {
      case MessageSenderType.SYSTEM:
        return this.translocoService.translate('Pixacare');
      case MessageSenderType.ALERT:
        return this.translocoService.translate('Alerte');
      default:
        return message.senderName ?? '';
    }
  }

  parseMessages(messages: QueryDocumentSnapshot<DocumentData, DocumentData>[]): Message[] {
    return messages.map((document) => {
      const data = document.data() as Message;
      return {
        ...data,
        id: document.id,
        senderName: this.getSenderName(data),
      };
    });
  }

  listenToMessages(channelId: string): void {
    const messagesDocRef = collection(this.channelQuery.firestore, `channels/${channelId}/messages`);
    const messagesQuery = query(messagesDocRef, orderBy('createdOn', 'desc'), endBefore(Timestamp.now()));

    this.MessageSubscription?.unsubscribe();
    this.MessageSubscription = collectionChanges(messagesQuery, { events: ['added'] })
      .pipe(untilDestroyed(this))
      .subscribe((changes) => {
        const newMessages = this.parseMessages(changes.map((change) => change.doc));

        if (newMessages.length === 0) {
          return;
        }

        this.messageGroupService.addMessages(newMessages, 'prepend', this.firstCursor);
        this.firstCursor = newMessages[0] as unknown as Message;

        this.hasUnseenMessages.next(true);
        this.currentMessages.next({
          ...this.indexMessagesById(newMessages),
          ...this.currentMessages.value,
        });
      });
  }

  async loadMessages(channelId: string, { reset } = { reset: true }): Promise<void> {
    if (reset) {
      this.listenToMessages(channelId);
      this.updateChannelUnreads(channelId);
    }

    const messagesDocRef = collection(this.channelQuery.firestore, `channels/${channelId}/messages`);
    const messagesQuery = query(
      messagesDocRef,
      orderBy('createdOn', 'desc'),
      ...(reset || !this.lastCursor ? [] : [startAfter(this.lastCursor.createdOn)]),
      limit(this.pageSize),
    );

    const messagesDocuments = await getDocs(messagesQuery);
    const messages = this.parseMessages(messagesDocuments.docs);

    const indexedMessages = this.indexMessagesById(messages);
    if (reset) {
      this.messageGroupService.reset();
      this.currentMessages.next(indexedMessages);
      this.firstCursor = messages[0] as unknown as Message;
    } else {
      this.currentMessages.next({ ...this.currentMessages.value, ...indexedMessages });
    }

    if (messages.length) {
      this.messageGroupService.addMessages(messages);
      this.lastCursor = messages.slice(-1)[0] as unknown as Message;
    }
  }

  async sendMessage(channelId: string, newMessage: DraftMessage): Promise<void> {
    if (newMessage.attachment?.sequence) {
      await this.sendSequence(channelId, newMessage);
    } else {
      await this.sendSimpleMessage(channelId, newMessage.text);
    }
  }

  searchChannels$(searchOptions: ChannelsSearchOptions): Observable<Channel[]> {
    return this.channels$.pipe(
      map((channels) => searchChannels(channels, this.authenticationService.user.id, searchOptions)),
    );
  }

  private async sendSimpleMessage(channelId: string, text: string): Promise<void> {
    const messagesDocRef = collection(this.channelQuery.firestore, `channels/${channelId}/messages`);
    await addDoc(messagesDocRef, {
      channelId,
      senderId: this.authenticationService.user.id,
      senderName: formatUsername(this.authenticationService.user),
      createdOn: Timestamp.now(),
      text,
      special: null,
    } as CreateMessageRequest);
  }

  private async sendSequence(channelId: string, newMessage: DraftMessage): Promise<void> {
    this.shareService
      .shareObject({
        clientCode: newMessage.attachment.sequence.clientCode,
        toMailAddresses: [],
        toUserIds: [],
        toChannelIds: [channelId],
        objectId: newMessage.attachment.sequence.sequenceInstance.id,
        objectType: ObjectType.SEQUENCE,
        withAdminPrivileges: false,
        message: newMessage.text,
        expiryInDays: 7,
      })
      .subscribe();
  }

  private indexMessagesById(messages: Message[]): { [messageId: string]: Message } {
    return messages.reduce((acc, message) => {
      acc[message.id] = message;
      return acc;
    }, {});
  }
}
